import * as tslib_1 from "tslib";
import { CHART_UPDATE_DELAY, CHART_UPDATE_RETRY_DELAY } from '../_constants/analytics.constants';
import * as i0 from "@angular/core";
var ChartUpdateService = /** @class */ (function () {
    function ChartUpdateService() {
        this.chartUpdateRetries = new Map();
        this.MAX_CHART_UPDATE_RETRIES = 3;
    }
    /**
     * Update a bar chart instance with new data
     */
    ChartUpdateService.prototype.updateBarChart = function (cdr, options) {
        var chartInstance = options.chartInstance, labels = options.labels, datasets = options.datasets, chartId = options.chartId, activeTab = options.activeTab, requiredTab = options.requiredTab, _a = options.isDestroyingCharts, isDestroyingCharts = _a === void 0 ? false : _a;
        // Only update if we're on the required tab
        if (activeTab !== requiredTab) {
            return;
        }
        // Don't update if charts are being destroyed
        if (isDestroyingCharts) {
            return;
        }
        if (chartInstance && chartInstance.chart) {
            // Reset retry counter on success
            this.chartUpdateRetries.delete(chartId);
            // Update chart data
            if (chartInstance.chart.data) {
                chartInstance.chart.data.labels = labels;
                chartInstance.chart.data.datasets = datasets;
            }
            else {
                // Create data object if it doesn't exist
                chartInstance.chart.data = {
                    labels: labels,
                    datasets: datasets
                };
            }
            chartInstance.chart.update('none'); // 'none' = no animation
            if (options.onSuccess) {
                options.onSuccess();
            }
        }
        else {
            this.handleRetry(cdr, options);
        }
    };
    /**
     * Update a donut chart instance with new data and options
     */
    ChartUpdateService.prototype.updateDonutChart = function (cdr, options) {
        var chartInstance = options.chartInstance, chartKey = options.chartKey, labels = options.labels, datasets = options.datasets, chartOptions = options.chartOptions, chartId = options.chartId, activeTab = options.activeTab, requiredTab = options.requiredTab, _a = options.isDestroyingCharts, isDestroyingCharts = _a === void 0 ? false : _a;
        // Only update if we're on the required tab
        if (activeTab !== requiredTab) {
            return;
        }
        // Don't update if chart key is 0 (chart is being destroyed/recreated)
        if (chartKey === 0) {
            return;
        }
        // Don't update if charts are being destroyed
        if (isDestroyingCharts) {
            return;
        }
        if (chartInstance && chartInstance.chart) {
            // Reset retry counter on success
            this.chartUpdateRetries.delete(chartId);
            // Update chart data
            if (chartInstance.chart.data) {
                chartInstance.chart.data.labels = labels;
                chartInstance.chart.data.datasets = datasets;
            }
            else {
                // Create data object if it doesn't exist
                chartInstance.chart.data = {
                    labels: labels,
                    datasets: datasets
                };
            }
            // Update chart options if provided
            if (chartOptions) {
                // Chart.js uses chart.config.options internally, so we need to update that
                if (chartInstance.chart.config && chartInstance.chart.config.options) {
                    Object.assign(chartInstance.chart.config.options, chartOptions);
                    Object.assign(chartInstance.chart.options, chartOptions);
                }
            }
            // Force chart to re-render with new options and data
            chartInstance.chart.update('active');
            // Force re-render to ensure plugin applies radius changes for concentric circles
            if (datasets.length > 1) {
                setTimeout(function () {
                    if (chartInstance && chartInstance.chart) {
                        chartInstance.chart.render();
                    }
                }, 50);
            }
            if (options.onSuccess) {
                options.onSuccess();
            }
        }
        else {
            this.handleRetry(cdr, options);
        }
    };
    /**
     * Update a line chart instance with new data
     */
    ChartUpdateService.prototype.updateLineChart = function (cdr, options) {
        var chartInstance = options.chartInstance, labels = options.labels, datasets = options.datasets, chartId = options.chartId, activeTab = options.activeTab, requiredTab = options.requiredTab, _a = options.isDestroyingCharts, isDestroyingCharts = _a === void 0 ? false : _a;
        // Only update if we're on the required tab
        if (activeTab !== requiredTab) {
            return;
        }
        // Don't update if charts are being destroyed
        if (isDestroyingCharts) {
            return;
        }
        if (chartInstance && chartInstance.chart) {
            // Reset retry counter on success
            this.chartUpdateRetries.delete(chartId);
            // Update chart data
            if (chartInstance.chart.data) {
                // Create new arrays to ensure chart detects changes
                chartInstance.chart.data.labels = labels.slice();
                chartInstance.chart.data.datasets = datasets.map(function (ds) { return (tslib_1.__assign({}, ds)); });
            }
            else {
                // If data doesn't exist, create it
                chartInstance.chart.data = {
                    labels: labels.slice(),
                    datasets: datasets.map(function (ds) { return (tslib_1.__assign({}, ds)); })
                };
            }
            // Force a full update to handle label changes
            chartInstance.chart.update('none'); // 'none' = no animation
            // Resize to ensure labels are properly displayed after update
            setTimeout(function () {
                if (chartInstance && chartInstance.chart) {
                    chartInstance.chart.resize();
                    // Force another update after resize to ensure labels render correctly
                    chartInstance.chart.update('none');
                }
            }, 100);
            if (options.onSuccess) {
                options.onSuccess();
            }
        }
        else {
            this.handleRetry(cdr, options);
        }
    };
    /**
     * Handle retry logic for chart updates
     */
    ChartUpdateService.prototype.handleRetry = function (cdr, options) {
        var _this = this;
        var retryCount = this.chartUpdateRetries.get(options.chartId) || 0;
        if (retryCount < this.MAX_CHART_UPDATE_RETRIES) {
            this.chartUpdateRetries.set(options.chartId, retryCount + 1);
            // Retry after a longer delay if chart isn't ready
            setTimeout(function () {
                // Retry the appropriate chart type
                if (options.chartOptions !== undefined) {
                    _this.updateDonutChart(cdr, options);
                }
                else if (options.datasets.length > 0 && options.datasets[0].fill !== undefined) {
                    _this.updateLineChart(cdr, options);
                }
                else {
                    _this.updateBarChart(cdr, options);
                }
            }, CHART_UPDATE_RETRY_DELAY);
        }
        else {
            // Silently stop retrying after max attempts
            this.chartUpdateRetries.delete(options.chartId);
            if (options.onRetryExhausted) {
                options.onRetryExhausted();
            }
        }
    };
    /**
     * Clear retry counter for a specific chart
     */
    ChartUpdateService.prototype.clearRetry = function (chartId) {
        this.chartUpdateRetries.delete(chartId);
    };
    /**
     * Clear all retry counters
     */
    ChartUpdateService.prototype.clearAllRetries = function () {
        this.chartUpdateRetries.clear();
    };
    /**
     * Destroy and recreate a donut chart when compare mode or dataset count changes
     */
    ChartUpdateService.prototype.destroyAndRecreateDonutChart = function (cdr, chartInstance, chartKeyRef, onRecreated) {
        // Destroy the existing chart instance
        if (chartInstance && chartInstance.chart) {
            chartInstance.chart.destroy();
            // Clear the chart reference so Angular recreates it
            chartInstance.chart = null;
        }
        // Temporarily set key to 0 to force destruction, then increment to force recreation
        chartKeyRef.value = 0;
        // Use markForCheck to schedule change detection without immediately triggering it
        cdr.markForCheck();
        // Use setTimeout to ensure destruction completes before triggering change detection
        setTimeout(function () {
            // Increment key to recreate chart
            chartKeyRef.value++;
            cdr.markForCheck();
            // Update the chart after recreation
            setTimeout(function () {
                onRecreated();
            }, CHART_UPDATE_DELAY);
        }, 50);
    };
    /**
     * Update chart view with change detection and delayed update
     */
    ChartUpdateService.prototype.updateChartView = function (cdr, updateFn, shouldDefer, onDefer) {
        if (shouldDefer === void 0) { shouldDefer = false; }
        if (shouldDefer && onDefer) {
            onDefer();
            return;
        }
        cdr.detectChanges();
        // Update the chart after a brief delay to ensure it's rendered
        setTimeout(function () {
            updateFn();
        }, CHART_UPDATE_DELAY);
    };
    /**
     * Recreate a bar chart by toggling data arrays
     * This forces Angular to destroy and recreate the chart with new data
     * Useful when the chart instance isn't initialized or when forcing a complete re-render
     *
     * @param cdr - ChangeDetectorRef for triggering change detection
     * @param getCurrentData - Function that returns current { labels, datasets, compareData }
     * @param setData - Function that sets new { labels, datasets, compareData }
     * @param chartId - Optional chart ID for debugging
     */
    ChartUpdateService.prototype.recreateBarChart = function (cdr, getCurrentData, setData, chartId) {
        // Store current data
        var currentData = getCurrentData();
        var tempLabels = currentData.labels;
        var tempDatasets = currentData.datasets;
        var tempCompareData = currentData.compareData || [];
        // Clear data to trigger *ngIf="false" and destroy the chart
        setData({ labels: [], datasets: [], compareData: [] });
        cdr.detectChanges();
        // Restore data to trigger *ngIf="true" and recreate the chart
        setTimeout(function () {
            setData({
                labels: tempLabels,
                datasets: tempDatasets,
                compareData: tempCompareData
            });
            cdr.detectChanges();
        }, 10);
    };
    ChartUpdateService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function ChartUpdateService_Factory() { return new ChartUpdateService(); }, token: ChartUpdateService, providedIn: "root" });
    return ChartUpdateService;
}());
export { ChartUpdateService };
